import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Save, ListChecks, AlertCircle, Lightbulb, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  getRiderCheckTaxonomy,
  getMyStructuredRiders,
  createMyStructuredRider,
  saveMyStructuredRider,
  deleteMyStructuredRider,
} from '../lib/api';
import { groupCategoriesForSelect } from '../constants/inventoryTemplates';

const AMBER = 'var(--s8-amber, #C8923E)';

const EMPTY_LINE = () => ({
  id: null,
  category: '',
  mode: 'model', // 'model' | 'class' — §6.1: model text OR explicit "any model in class"
  model: '',
  any_in_class: '',
  quantity: '1',
  channels: '',
  bandBrings: false,
});

// Client-side mirror of the shared validator for instant feedback while
// typing. The server validator (rider_check_v2.py) is authoritative — same
// rules, same message shape: where + what's wrong + how to fix + example.
function lineIssues(line, idx, isCapacity) {
  const issues = [];
  const where = `Line ${idx + 1}`;
  if (!line.category) {
    issues.push(`${where} has no category. Pick one from the list, e.g. 'Guitar amp'.`);
    return issues;
  }
  if (isCapacity(line.category)) {
    const ch = parseInt(line.channels, 10);
    if (!ch || ch < 1) issues.push(`${where} needs a channel total. Add the number you need, e.g. 24.`);
    return issues;
  }
  const q = parseInt(line.quantity, 10);
  if (!q || q < 1) issues.push(`${where} is missing a quantity. Add how many you need, e.g. 2× Fender Twin.`);
  if (line.mode === 'class') {
    if (!line.any_in_class) issues.push(`${where} needs an equivalence class. Pick one, e.g. 'Standard dynamic vocal mic'.`);
  } else if (!line.model || line.model.trim().length < 2) {
    issues.push(`${where} needs a model. Type one (e.g. Shure SM58), or switch to 'any in class' so swaps can be checked.`);
  }
  return issues;
}

// Server line shape → the editor's flat, all-strings form state.
function toEditorLines(rider) {
  return (rider?.lines || []).map((l) => ({
    id: l.id,
    category: l.category || '',
    mode: l.any_in_class ? 'class' : 'model',
    model: l.model || '',
    any_in_class: l.any_in_class || '',
    quantity: l.quantity != null ? String(l.quantity) : '1',
    channels: l.attributes?.channels != null ? String(l.attributes.channels) : '',
    bandBrings: l.provided_by === 'band',
  }));
}

export default function MyRiderPage() {
  const [taxonomy, setTaxonomy] = useState(null);
  const [variants, setVariants] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [lines, setLines] = useState([]);
  const [version, setVersion] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [serverTips, setServerTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isCapacity = useCallback(
    (category) => {
      const cat = taxonomy?.categories?.find((c) => c.value === category);
      return cat?.match_type === 'capacity';
    },
    [taxonomy]
  );

  const showVariant = useCallback((entry) => {
    setActiveId(entry.rider.variantId);
    setVersion(entry.rider.version);
    setSavedAt(entry.rider.createdAt);
    setLines(toEditorLines(entry.rider));
    setServerTips(entry.validation?.tips || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [taxRes, ridersRes] = await Promise.all([
          getRiderCheckTaxonomy(), getMyStructuredRiders(),
        ]);
        setTaxonomy(taxRes.data);
        const list = ridersRes.data?.riders || [];
        setVariants(list);
        if (list.length) showVariant(list[0]);
      } catch {
        toast.error('Failed to load riders');
      } finally {
        setLoading(false);
      }
    })();
  }, [showVariant]);

  const handleNewVariant = async () => {
    const name = window.prompt('Name this rider (e.g. "Acoustic trio")');
    if (!name?.trim()) return;
    try {
      const res = await createMyStructuredRider(name.trim());
      setVariants((prev) => [...prev, res.data]);
      showVariant(res.data);
      toast.success(`Created "${res.data.rider.name}"`);
    } catch {
      toast.error('Could not create that rider');
    }
  };

  const handleDeleteVariant = async () => {
    const entry = variants.find((v) => v.rider.variantId === activeId);
    if (!entry) return;
    if (!window.confirm(`Delete "${entry.rider.name}" and all its versions?`)) return;
    try {
      await deleteMyStructuredRider(activeId);
      const remaining = variants.filter((v) => v.rider.variantId !== activeId);
      setVariants(remaining);
      if (remaining.length) showVariant(remaining[0]);
      toast.success(`Deleted "${entry.rider.name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete that rider');
    }
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const classesForCategory = (category) =>
    (taxonomy?.classes || []).filter((c) => c.category === category);

  const groupedCategories = groupCategoriesForSelect(taxonomy?.categories);

  const allIssues = lines.map((l, i) => lineIssues(l, i, isCapacity));
  const readyCount = allIssues.filter((iss) => iss.length === 0).length;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = lines.map((l) => {
        const base = {
          id: l.id || undefined,
          category: l.category || null,
          provided_by: l.bandBrings ? 'band' : 'venue',
        };
        if (isCapacity(l.category)) {
          return { ...base, attributes: { channels: parseInt(l.channels, 10) || null } };
        }
        return {
          ...base,
          model: l.mode === 'model' ? (l.model || null) : null,
          any_in_class: l.mode === 'class' ? (l.any_in_class || null) : null,
          quantity: parseInt(l.quantity, 10) || null,
        };
      });
      const res = await saveMyStructuredRider(activeId, payload);
      const rider = res.data?.rider;
      setVersion(rider?.version);
      setSavedAt(rider?.createdAt);
      setServerTips(res.data?.validation?.tips || []);
      setLines((prev) => prev.map((l, i) => ({ ...l, id: rider?.lines?.[i]?.id || l.id })));
      setVariants((prev) => prev.map((v) =>
        v.rider.variantId === activeId ? { rider, validation: res.data?.validation } : v));
      const invalid = lines.length - readyCount;
      toast.success(
        invalid > 0
          ? `Saved as version ${rider?.version} — ${invalid} line${invalid === 1 ? '' : 's'} not check-ready yet`
          : `Saved as version ${rider?.version}`
      );
    } catch {
      toast.error('Failed to save rider');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'h-10 bg-[#151515] border-white/[0.08] text-white text-sm';
  const labelCls = 'text-[10px] uppercase tracking-wide text-white/40';

  if (loading) {
    return (
      <div className="content-container">
        <div className="h-40 rounded-lg bg-white/[0.04] animate-pulse mt-6" />
      </div>
    );
  }

  return (
    <div className="content-container" data-testid="my-rider-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="page-title gold-accent">Technical Rider</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Structured equipment lines, checked against a venue&apos;s equipment. Keep one per line-up —
            each is versioned separately, so editing the trio never touches the full band.
          </p>
        </div>
        {version && (
          <div
            className="text-right"
            style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#a39889' }}
            data-testid="rider-version-info"
          >
            <div>version {version}</div>
            {savedAt && <div>saved {new Date(savedAt).toLocaleString()}</div>}
          </div>
        )}
      </div>

      {/* Which rider you are editing */}
      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="rider-variant-bar">
        {variants.map(({ rider }) => {
          const active = rider.variantId === activeId;
          return (
            <button
              key={rider.variantId}
              type="button"
              onClick={() => showVariant(variants.find((v) => v.rider.variantId === rider.variantId))}
              data-testid={`rider-variant-${rider.variantId}`}
              aria-pressed={active}
              style={{
                fontFamily: "'DM Mono', monospace", fontSize: 12,
                padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                background: active ? 'rgba(200, 146, 62, 0.16)' : 'transparent',
                color: active ? '#E8C088' : '#a39889',
                border: `1px solid ${active ? 'rgba(200, 146, 62, 0.42)' : 'rgba(232, 226, 214, 0.14)'}`,
              }}
            >
              {rider.name}
              <span style={{ color: '#6f675c' }}> · {rider.lines?.length || 0}</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={handleNewVariant}
          data-testid="new-rider-variant"
          style={{
            fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '7px 14px',
            borderRadius: 999, cursor: 'pointer', background: 'transparent', color: '#a39889',
            border: '1px dashed rgba(232, 226, 214, 0.18)',
          }}
        >
          + New Rider
        </button>
        {variants.length > 1 && activeId && (
          <button
            type="button"
            onClick={handleDeleteVariant}
            data-testid="delete-rider-variant"
            style={{
              fontFamily: "'DM Mono', monospace", fontSize: 12, padding: '7px 12px',
              borderRadius: 999, cursor: 'pointer', background: 'transparent',
              color: '#a39889', border: '1px solid transparent',
            }}
          >
            <Trash2 size={13} aria-hidden style={{ display: 'inline', marginRight: 5 }} />
            Delete
          </button>
        )}
      </div>

      {!loading && variants.length === 0 && (
        <div className="mt-6" data-testid="no-riders-yet">
          <p className="text-sm text-muted-foreground">
            No riders yet. Create one — name it after the line-up it covers, like
            &ldquo;Full band&rdquo; or &ldquo;Acoustic trio&rdquo;.
          </p>
        </div>
      )}

      {/* Check-ready progress (coach mode, §6.3) */}
      {lines.length > 0 && (
        <div
          className="mt-6 flex items-center gap-3"
          data-testid="rider-progress"
          style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}
        >
          <ListChecks size={15} style={{ color: AMBER }} />
          <span style={{ color: readyCount === lines.length ? '#5A9A6B' : '#C8923E' }}>
            {readyCount}/{lines.length} lines check-ready
          </span>
          {readyCount < lines.length && (
            <span className="text-white/40">— lines with issues are excluded from checks until fixed</span>
          )}
        </div>
      )}

      {/* Lines */}
      <div className="mt-4 space-y-2">
        {lines.length === 0 && (
          <p className="text-sm text-white/40">
            No rider lines yet. Add what you need on stage — amps, kit, mics, DIs, console channels.
          </p>
        )}
        {lines.map((line, idx) => {
          const issues = allIssues[idx];
          const cap = isCapacity(line.category);
          const classes = classesForCategory(line.category);
          return (
            <Card key={line.id || `new-${idx}`} className="bg-[#141414] border-white/[0.08]" data-testid={`rider-line-${idx}`}>
              <CardContent className="p-3">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[170px] flex-1 space-y-1">
                    <Label className={labelCls}>Category</Label>
                    <Select
                      value={line.category}
                      onValueChange={(v) => updateLine(idx, { category: v, any_in_class: '' })}
                    >
                      <SelectTrigger className={inputCls} data-testid={`rider-category-${idx}`}>
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {groupedCategories.map((group) => (
                          <SelectGroup key={group.label}>
                            <SelectLabel>{group.label}</SelectLabel>
                            {group.options.map((c) => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {cap ? (
                    <div className="w-32 space-y-1">
                      <Label className={labelCls}>Channels Needed</Label>
                      <Input
                        type="number"
                        min="1"
                        value={line.channels}
                        onChange={(e) => updateLine(idx, { channels: e.target.value })}
                        placeholder="24"
                        className={inputCls}
                        data-testid={`rider-channels-${idx}`}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="min-w-[130px] space-y-1">
                        <Label className={labelCls}>Specify</Label>
                        <Select value={line.mode} onValueChange={(v) => updateLine(idx, { mode: v })}>
                          <SelectTrigger className={inputCls} data-testid={`rider-mode-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="model">Specific model</SelectItem>
                            <SelectItem value="class">Any in class</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {line.mode === 'class' ? (
                        <div className="min-w-[200px] flex-[2] space-y-1">
                          <Label className={labelCls}>Equivalence Class</Label>
                          <Select
                            value={line.any_in_class}
                            onValueChange={(v) => updateLine(idx, { any_in_class: v })}
                          >
                            <SelectTrigger className={inputCls} data-testid={`rider-class-${idx}`}>
                              <SelectValue placeholder={line.category ? 'Pick a class' : 'Pick a category first'} />
                            </SelectTrigger>
                            <SelectContent>
                              {classes.length === 0 && line.category && (
                                <SelectItem value="__none__" disabled>No classes for this category</SelectItem>
                              )}
                              {classes.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div className="min-w-[200px] flex-[2] space-y-1">
                          <Label className={labelCls}>Model</Label>
                          <Input
                            value={line.model}
                            onChange={(e) => updateLine(idx, { model: e.target.value })}
                            placeholder="e.g. Fender Twin Reverb"
                            className={inputCls}
                            data-testid={`rider-model-${idx}`}
                          />
                        </div>
                      )}
                      <div className="w-20 space-y-1">
                        <Label className={labelCls}>Qty</Label>
                        <Input
                          type="number"
                          min="1"
                          value={line.quantity}
                          onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                          className={inputCls}
                          data-testid={`rider-quantity-${idx}`}
                        />
                      </div>
                    </>
                  )}

                  <label
                    className="flex items-center gap-2 mb-2 cursor-pointer select-none"
                    style={{ fontFamily: "'Barlow', sans-serif", fontSize: 12.5, color: '#a39889' }}
                    title="Your own gear — never checked against the venue's equipment"
                  >
                    <input
                      type="checkbox"
                      checked={line.bandBrings}
                      onChange={(e) => updateLine(idx, { bandBrings: e.target.checked })}
                      data-testid={`rider-band-brings-${idx}`}
                    />
                    We bring this
                  </label>

                  <button
                    type="button"
                    onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                    title="Remove line"
                    className="p-1.5 rounded hover:bg-red-500/20 transition-colors mb-0.5"
                    data-testid={`rider-remove-${idx}`}
                  >
                    <Trash2 size={15} className="text-white/40" />
                  </button>
                </div>

                {issues.length > 0 && (
                  <div className="mt-2 space-y-1" data-testid={`rider-issues-${idx}`}>
                    {issues.map((msg, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs" style={{ color: '#E5A89F' }}>
                        <AlertCircle size={13} className="mt-0.5 shrink-0" />
                        {msg}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setLines((prev) => [...prev, EMPTY_LINE()])}
        className="mt-3 flex items-center gap-1.5 text-sm transition-colors hover:opacity-80"
        style={{ color: AMBER }}
        data-testid="rider-add-line-btn"
      >
        <Plus size={15} /> Add rider line
      </button>

      {/* Coaching tips (non-blocking, §6.2) */}
      {serverTips.length > 0 && (
        <Card className="mt-6 bg-card border-border" data-testid="rider-tips">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm" style={{ color: AMBER }}>
              <Lightbulb size={15} /> Tips
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-1.5 text-xs text-white/60">
              {serverTips.map((tip, i) => (
                <li key={i}>{tip}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="mt-8 pb-8 flex flex-wrap items-center gap-4">
        <Button
          onClick={handleSave}
          disabled={saving || lines.length === 0 || !activeId}
          className="h-12 px-8 font-bold uppercase tracking-wide"
          style={{ background: AMBER, color: '#0E1110' }}
          data-testid="rider-save-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving…' : version ? 'Save new version' : 'Save rider'}
        </Button>
        <Link
          to="/rider-check"
          className="inline-flex items-center gap-1.5 text-sm hover:opacity-80 transition-opacity"
          style={{ color: AMBER }}
          data-testid="rider-run-check-link"
        >
          Run a compatibility check <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
